<script setup lang="ts">
    import type { ClientLogo } from "~/utils/clientLogos.utils";

    withDefaults(defineProps<{
        logos: readonly ClientLogo[];
        alts?: Record<string, string>;
        // In home l'animazione parte da WorkWithUs con GSAP; altrove parte subito
        autoplay?: boolean;
    }>(), {
        alts: () => ({}),
        autoplay: false,
    });

    const globalState = useGlobalState();
</script>

<template>
    <div class="pdg-container-logos" :class="{ 'is-playing': autoplay }">
        <div class="pdg-slider-work-with-us-gradient left"></div>
        <div class="pdg-slider-container">
            <NuxtImg
                v-for="(item, index) in logos"
                class="pdg-logo"
                :alt="alts[index]"
                :src="`/assets/img/${globalState.mode}/work-with-us/${item}.svg`"
                width="400"
                height="300"
                loading="lazy"
            />
        </div>
        <div class="pdg-slider-work-with-us-gradient"></div>
    </div>
</template>

<style scoped lang="scss">
    @import "../scss/includes.scss";

    .pdg-container-logos {
        overflow-x: hidden;
        white-space: nowrap;
        position: relative;

        .pdg-slider-work-with-us-gradient {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: rem(64);
            // Colore dello sfondo su cui sta lo slider: di default il blu della home
            background: linear-gradient(-90deg, var(--pdg-logo-slider-bg, var(--bg-4)), var(--pdg-logo-slider-bg-transparent, rgba(#246399, 0)));
            z-index: 1;
            pointer-events: none;

            @media screen and (min-width: map-get($breakpoints, sm)) {
                width: rem(72);
            }

            @media screen and (min-width: map-get($breakpoints, md)) {
                width: rem(236);
            }

            &.left {
                right: initial;
                left: 0;
                background: linear-gradient(90deg, var(--pdg-logo-slider-bg, var(--bg-4)), var(--pdg-logo-slider-bg-transparent, rgba(#246399, 0)));
            }
        }

        .pdg-slider-container {
            display: grid;
            gap: 8px;
            grid-template-columns: repeat(4, 1fr); /* number of visible images + width */
            overflow: hidden;

            @media screen and (min-width: map-get($breakpoints, sm)) {
                gap: 12px;
                grid-template-columns: repeat(6, 1fr); /* number of visible images + width */
            }

            @media screen and (min-width: map-get($breakpoints, md)) {
                gap: 24px;
                grid-template-columns: repeat(8, 1fr); /* number of visible images + width */
            }

            @media screen and (min-width: map-get($breakpoints, lg)) {
                grid-template-columns: repeat(10, 1fr); /* number of visible images + width */
            }

            $n: 26;
            /* reference: https://verpex.com/blog/website-tips/how-to-create-a-css-only-infinite-scroll-animation */

            @keyframes r {
                #{calc(100% / ($n + ($n - 2) * 0.24))} {
                    transform: translate(-100%);
                    opacity: 0;
                }
                #{calc(100% / ($n + ($n - 2) * 0.24) + 0.1%)} {
                    transform: translate(calc(($n - 1 + ($n - 2) * 0.24) * 100%));
                    opacity: 0;
                }
                #{calc(100% / ($n + ($n - 2) * 0.24) + 0.2%)} {
                    opacity: 1;
                }
            }

            @for $i from 1 to ($n + 2) {
                & > .pdg-logo:nth-child(#{$i}) {
                    animation-delay: #{calc((1 - $i) / $n) * 40s}
                }
            }

            .pdg-logo {
                height: rem(68);
                width: auto;
                grid-area: 1/1;
                width: 100%;
                animation: r 40s linear infinite;
                animation-play-state: paused;
                -webkit-animation-play-state: paused;

                @media screen and (min-width: map-get($breakpoints, md)) {
                    height: rem(120);
                }
            }
        }

        &.is-playing .pdg-slider-container .pdg-logo {
            animation-play-state: running;
            -webkit-animation-play-state: running;
        }

    }
</style>
